import type { IndustryContent } from '@/types/content';
import { Section, SectionHeader, SectionItem } from './Section';
import { FeatureCard, TestimonialCard } from './Card';
import { LeadForm } from './LeadForm';
import { SEO } from './SEO';

interface IndustryTemplateProps {
  content: IndustryContent;
}

export function IndustryTemplate({ content }: IndustryTemplateProps) {
  return (
    <>
      <SEO
        title={content.seo.title}
        description={content.seo.description}
        keywords={content.seo.keywords}
        ogImage={`/og/industries/${content.slug}.png`}
        type="website"
      />

      {/* Hero Section */}
      <Section background="ink" className="pt-24 pb-16 md:pt-32 md:pb-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <SectionItem>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-sm mb-6">
              <span className="w-2 h-2 rounded-full bg-accent" />
              {content.name} Software
            </div>
            <h1 className="font-display text-hero text-paper mb-6">{content.headline}</h1>
            <p className="text-xl text-gray-300 mb-8">{content.subheadline}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="/demo" className="btn-primary">
                Request Demo
              </a>
              <a href="#features" className="btn-secondary border-white text-white hover:bg-white hover:text-ink">
                See Features
              </a>
            </div>
          </SectionItem>

          <SectionItem>
            <div className="relative">
              <div className="aspect-video rounded-2xl bg-charcoal overflow-hidden">
                {content.heroImageUrl ? (
                  <img
                    src={content.heroImageUrl}
                    alt={`${content.name} software dashboard`}
                    width={800}
                    height={450}
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-500">
                    <span>{content.name} Dashboard Preview</span>
                  </div>
                )}
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 rounded-2xl bg-accent/20 -z-10" />
              <div className="absolute -top-4 -left-4 w-20 h-20 rounded-xl bg-accent/10 -z-10" />
            </div>
          </SectionItem>
        </div>
      </Section>

      {/* Pain Points Section */}
      <Section background="cream">
        <SectionHeader
          title="Sound familiar?"
          subtitle={`Common challenges ${content.name.toLowerCase()} companies face every day`}
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {content.painPoints.map((painPoint, i) => (
            <SectionItem key={i}>
              <div className="text-center">
                <div className="w-14 h-14 rounded-xl bg-white shadow-soft mx-auto mb-4 flex items-center justify-center">
                  <PainIcon className="w-7 h-7 text-accent" />
                </div>
                <h3 className="font-heading font-semibold mb-2">{painPoint.title}</h3>
                <p className="text-sm text-muted">{painPoint.description}</p>
              </div>
            </SectionItem>
          ))}
        </div>
      </Section>

      {/* Features Section */}
      <Section id="features">
        <SectionHeader
          title={`Built for ${content.name}`}
          subtitle="Industry-specific features that solve your biggest challenges"
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {content.features.map((feature, i) => (
            <SectionItem key={i}>
              <FeatureCard
                icon={<FeatureIcon className="w-6 h-6" />}
                title={feature.title}
                description={feature.description}
              />
            </SectionItem>
          ))}
        </div>
      </Section>

      {/* Stats Section */}
      <Section background="accent">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {content.stats.map((stat, i) => (
            <SectionItem key={i}>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-white/80">{stat.label}</div>
              </div>
            </SectionItem>
          ))}
        </div>
      </Section>

      {/* Testimonial Section */}
      {content.testimonial && (
        <Section background="cream">
          <div className="max-w-3xl mx-auto">
            <SectionItem>
              <TestimonialCard
                quote={content.testimonial.quote}
                author={content.testimonial.author}
                role={content.testimonial.role}
                company={content.testimonial.company}
                stat={content.testimonial.stats}
                className="text-center"
              />
            </SectionItem>
          </div>
        </Section>
      )}

      {/* CTA Section */}
      <Section background="ink">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <SectionItem>
            <h2 className="font-display text-h2 text-paper mb-4">
              Ready to transform your {content.name.toLowerCase()} business?
            </h2>
            <p className="text-gray-300 mb-6">
              Get a personalized demo tailored to {content.name.toLowerCase()} operations.
              See how Omniforge can help you dispatch smarter, get paid faster, and grow your business.
            </p>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-center gap-2">
                <CheckIcon className="w-5 h-5 text-success" />
                30-minute personalized demo
              </li>
              <li className="flex items-center gap-2">
                <CheckIcon className="w-5 h-5 text-success" />
                {content.name}-specific workflow review
              </li>
              <li className="flex items-center gap-2">
                <CheckIcon className="w-5 h-5 text-success" />
                Implementation roadmap
              </li>
            </ul>
          </SectionItem>

          <SectionItem>
            <div className="bg-white rounded-2xl p-6 md:p-8">
              <h3 className="font-heading font-semibold text-xl mb-6">
                Request Your {content.name} Demo
              </h3>
              <LeadForm mode="demo" />
            </div>
          </SectionItem>
        </div>
      </Section>
    </>
  );
}

// Icons
function PainIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function FeatureIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  );
}
